import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./aotdIrlLanding.css";

// The AOTD IRL frame's own page: what it is, how a day goes, how it gets set up,
// and a buy button that opens the frame's product in the store. The price lives
// only on the store product, so this page never shows one.

export const AOTD_IRL_PRODUCT_ID = "8d3a80c1-59c4-4daf-bfc4-bcfd438836ca";
const PRODUCT_PATH = `/marketplace/${AOTD_IRL_PRODUCT_ID}`;

interface Photo {
  src: string;
  alt: string;
  note?: string;
}

const PHOTOS: Photo[] = [
  { src: "/irl-frame/irl-front-studio.jpg", alt: "The AOTD IRL frame from the front, showing an Art of the Day" },
  { src: "/irl-frame/irl-detail-labels.jpg", alt: "The two small e-paper labels under the art" },
  { src: "/irl-frame/irl-desk-scale.jpg", alt: "The frame on a desk next to a coffee cup" },
  { src: "/irl-frame/irl-wall-living.jpg", alt: "The frame hanging on a living room wall" },
  { src: "/irl-frame/irl-shelf-leaning.jpg", alt: "The frame leaning on a shelf" },
  { src: "/irl-frame/irl-at-home.jpg", alt: "Phone photo of the first frame at home", note: "the first one, phone photo" },
];

const SPECS: [string, string][] = [
  ["screen", '13.3" Spectra 6 colour e-paper'],
  ["pixels", "1200 x 1600, portrait"],
  ["inks", "black, white, red, yellow, blue, green"],
  ["labels", "two small e-paper screens: author, date, title, emojis, QR"],
  ["inside", "ESP32-S3 with wifi, updates itself over the air"],
  ["power", "plugs in with the included cable; the picture stays even when unplugged"],
  ["frame", "solid wood, screwed together, made to order"],
];

const INKS = ["#222", "#fff", "#d2322d", "#f2c230", "#2a59b8", "#2f8a4c"];

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// ---------- the buy animation ----------

// A Spectra 6 panel redraws by flashing its old picture inverted, then laying
// down each ink in a pass that scans from top to bottom. The page does the same:
// two inverted flashes, six ink passes, a last grey pass, and the store opens
// under the grey.
function playRefresh(done: () => void) {
  const root = document.documentElement;
  [0, 110, 220, 330].forEach((at, i) => {
    window.setTimeout(() => root.classList.toggle("irl-fx-invert", i % 2 === 0), at);
  });

  const el = document.createElement("div");
  el.className = "irl-fx-refresh";
  [...INKS, "#f0f0f0"].forEach((c, i) => {
    const pass = document.createElement("span");
    pass.style.backgroundColor = c;
    pass.style.animationDelay = `${380 + i * 130}ms`;
    if (i === INKS.length) pass.className = "is-clear";
    el.appendChild(pass);
  });
  document.body.appendChild(el);

  window.setTimeout(done, 1340);
  window.setTimeout(() => el.classList.add("is-out"), 1380);
  window.setTimeout(() => el.remove(), 1820);
}

const BuyButton: React.FC<{ big?: boolean }> = ({ big }) => {
  const navigate = useNavigate();
  const busy = useRef(false);

  const go = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (busy.current) return;
    const open = () => {
      navigate(PRODUCT_PATH);
      window.scrollTo(0, 0);
    };
    if (reducedMotion()) return open();
    busy.current = true;
    playRefresh(open);
  };

  return (
    <a href={PRODUCT_PATH} onClick={go} className={`irl-buy${big ? " irl-buy--big" : ""}`}>
      <span className="irl-stretch">BUY</span>
    </a>
  );
};

const AotdIrlLanding: React.FC = () => {
  const [shown, setShown] = useState(0);
  const photo = PHOTOS[shown];

  return (
    <div className="irl-page">
      <section className="irl-hero">
        <div className="irl-gallery">
          <div className="irl-media">
            <img src={photo.src} alt={photo.alt} />
            {photo.note && <span className="irl-media-note">{photo.note}</span>}
          </div>
          <div className="irl-thumbs" role="list">
            {PHOTOS.map((p, i) => (
              <button
                key={p.src}
                type="button"
                role="listitem"
                className={i === shown ? "is-on" : undefined}
                onClick={() => setShown(i)}
                aria-label={`show photo ${i + 1}`}
              >
                <img src={p.src} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        <div className="irl-copy">
          <p className="irl-kicker">art of the day, in real life</p>
          <h1 className="irl-title">AOTD IRL</h1>
          <p className="irl-lead">
            A 13.3" colour e-paper frame in solid wood. Every day, when the new Art of the Day unlocks on
            n450s.com, it wakes up, draws it, and goes back to sleep.
          </p>
          <ul className="irl-points">
            <li>no glow, no backlight: it looks like a print</li>
            <li>six real inks on 1200 x 1600 pixels</li>
            <li>the author, the date, the title and the emojis on two small screens under it</li>
            <li>set up from your phone in a few minutes</li>
          </ul>
          <div className="irl-actions">
            <BuyButton />
            <p className="irl-actions-note">made to order · price, delivery and payment on the store page</p>
          </div>
          <p className="irl-have-one">
            already have one? <Link to="/aotd-irl/setup">set it up →</Link>
          </p>
        </div>
      </section>

      <section className="irl-split">
        <div className="irl-split-media">
          <img src="/irl-frame/irl-detail-labels.jpg" alt="Close up of the labels under the art" loading="lazy" />
        </div>
        <div className="irl-split-copy">
          <h2 className="irl-h2">what's in it</h2>
          <dl className="irl-specs">
            {SPECS.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          <div className="irl-inks" aria-label="the six inks">
            {INKS.map((c) => (
              <span key={c} style={{ background: c }} />
            ))}
          </div>
        </div>
      </section>

      <section className="irl-day">
        <h2 className="irl-h2">how a day goes</h2>
        <ol className="irl-steps">
          <li>
            <b>the day unlocks</b>
            <span>someone wins the morning race on n450s.com and their art of the day is made.</span>
          </li>
          <li>
            <b>the frame wakes</b>
            <span>it asks the server what to show, downloads the day's picture and redraws the panel.</span>
          </li>
          <li>
            <b>it sleeps</b>
            <span>e-paper keeps the picture with no power at all, so the art just sits there all day.</span>
          </li>
          <li>
            <b>tomorrow, a new one</b>
            <span>the server tells it when to wake next, and new firmware arrives the same way.</span>
          </li>
        </ol>
      </section>

      <section className="irl-split irl-split--flip">
        <div className="irl-split-media">
          <img src="/irl-frame/irl-wall-living.jpg" alt="The frame on a wall" loading="lazy" />
        </div>
        <div className="irl-split-copy">
          <h2 className="irl-h2">from box to wall</h2>
          <ol className="irl-setup">
            <li>
              <b>plug it in.</b> the panel shows a setup screen after about ten seconds.
            </li>
            <li>
              <b>join its wifi.</b> on your phone pick <code>AOTD-Setup</code> and type your home wifi into the page
              that opens.
            </li>
            <li>
              <b>scan the QR.</b> it appears on the frame and makes the frame yours on your n450s account.
            </li>
            <li>
              <b>that's it.</b> rename it, check on it or hand it over to someone else from{" "}
              <Link to="/aotd-irl/my">your frames</Link>.
            </li>
          </ol>
          <Link to="/aotd-irl/setup" className="irl-link">
            full setup guide →
          </Link>
        </div>
      </section>

      <section className="irl-rooms" aria-label="in a room">
        {["/irl-frame/irl-shelf-leaning.jpg", "/irl-frame/irl-desk-scale.jpg", "/irl-frame/irl-at-home.jpg"].map((src) => (
          <img key={src} src={src} alt="" loading="lazy" />
        ))}
      </section>

      <section className="irl-end">
        <h2 className="irl-end-title">one frame, a new picture every day</h2>
        <p className="irl-end-sub">each frame is built when you order it.</p>
        <BuyButton big />
      </section>
    </div>
  );
};

export default AotdIrlLanding;
